import { useState } from 'react';

import { useQueries } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { BsCheckCircle, BsChevronDown, BsExclamationTriangle, BsXLg } from 'react-icons/bs';

import Popover from '#globalComponents/Overlay/Popover';

import { getQueueNameForJobType } from '../../../../../api/jobsApi';
import { JOB_TYPE_NAMES } from '../../../../../constants/jobTypeNames';
import { jobProgressOptions } from '../../../../../models/jobs/queries';

const isCancelling = (job) => job.status === 'aborted' && !job.finishedAt;
const isUnfinished = (job) => job.status === 'queued' || job.status === 'running' || isCancelling(job);

const jobLabel = (job) => job.label || JOB_TYPE_NAMES[job.jobType] || job.jobType;

const jobStatusText = (job, percent, attempt) => {
	if (job.indeterminate) return 'läuft …';
	if (isCancelling(job)) return 'Wird abgebrochen…';
	switch (job.status) {
		case 'queued':
			return attempt > 1 ? `Neuer Versuch (${attempt})` : 'Warteschlange';
		case 'running':
			return `${Math.round(percent)}%`;
		case 'completed':
			return 'Abgeschlossen';
		case 'failed':
			return 'Fehlgeschlagen';
		case 'aborted':
			return 'Abgebrochen';
		default:
			return job.status;
	}
};

const DOT_COLORS = {
	completed: 'bg-success-500',
	failed: 'bg-warning-500',
	aborted: 'bg-caution-500',
};

const Pill = ({ className, children }) => (
	<span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${className}`}>{children}</span>
);

Pill.propTypes = {
	className: PropTypes.string,
	children: PropTypes.node,
};

/**
 * All jobs of the selected query in one compact strip: aggregate state, averaged progress of the
 * running jobs, count pills per outcome, and a per-job breakdown behind "Details". `extraJobs`
 * are entries without a Bull job (the assistant's work while its popup is closed): same shape,
 * own `label`, no progress polling.
 */
export const JobProgressList = ({ runningJobs, extraJobs }) => {
	const [detailsOpen, setDetailsOpen] = useState(false);
	const [dismissedAt, setDismissedAt] = useState(null);

	const jobs = [...(runningJobs ?? []), ...(extraJobs ?? [])].filter(
		(job) => isUnfinished(job) || !dismissedAt || new Date(job.finishedAt) > dismissedAt,
	);

	// Per-job percent lives in the Bull progress endpoint, not in the job list — poll it for
	// every unfinished job that has a pollable queue.
	const pollable = jobs.filter((job) => isUnfinished(job) && getQueueNameForJobType(job.jobType));
	const progressResults = useQueries({
		queries: pollable.map((job) => jobProgressOptions(getQueueNameForJobType(job.jobType), job.bullJobId)),
	});
	const progressByJobId = Object.fromEntries(pollable.map((job, index) => [job.id, progressResults[index]?.data]));

	if (!jobs.length) return null;

	const percentOf = (job) => (job.status === 'completed' ? 100 : progressByJobId[job.id]?.progress?.percent || 0);

	const running = jobs.filter(isUnfinished);
	const done = jobs.filter((job) => job.status === 'completed');
	const failed = jobs.filter((job) => job.status === 'failed');
	const cancelled = jobs.filter((job) => job.status === 'aborted' && job.finishedAt);

	const measured = running.filter((job) => !job.indeterminate);
	const avgPercent = measured.length
		? Math.round(measured.reduce((sum, job) => sum + percentOf(job), 0) / measured.length)
		: 100;
	const lead = running[0] || failed[0] || cancelled[0] || done[done.length - 1];
	const leadSuffix = isUnfinished(lead) && !isCancelling(lead) ? '' : ` – ${jobStatusText(lead, percentOf(lead), 0)}`;

	let statusIcon = <BsCheckCircle className="h-4 w-4 flex-shrink-0 text-success-700" />;
	if (running.length > 0) {
		statusIcon = (
			<span className="h-3.5 w-3.5 flex-shrink-0 animate-spin rounded-full border-2 border-primary-200 border-t-primary-600" />
		);
	} else if (failed.length > 0) {
		statusIcon = <BsExclamationTriangle className="h-4 w-4 flex-shrink-0 text-warning-500" />;
	} else if (cancelled.length > 0) {
		statusIcon = <BsExclamationTriangle className="h-4 w-4 flex-shrink-0 text-caution-500" />;
	}

	return (
		<div className="mb-3 flex h-9 items-center gap-3 rounded-card border border-bgBase-500 bg-white px-3">
			{statusIcon}
			<span className="min-w-0 truncate text-sm text-text">
				{jobLabel(lead)}
				{leadSuffix}
			</span>
			{measured.length > 0 && (
				<div className="flex flex-shrink-0 items-center gap-2">
					<div className="h-1.5 w-28 overflow-hidden rounded-full bg-bgBase-500">
						<div
							className="h-full bg-primary-500 transition-all duration-300"
							style={{ width: `${Math.max(5, avgPercent)}%` }}
						/>
					</div>
					<span className="w-8 text-xs tabular-nums text-bgBase-800">{avgPercent}%</span>
				</div>
			)}
			<div className="ml-auto flex flex-shrink-0 items-center gap-1.5">
				{running.length > 1 && <Pill className="bg-primary-200 text-primary-700">{running.length} laufen</Pill>}
				{done.length > 0 && <Pill className="bg-success-200 text-success-700">{done.length} fertig</Pill>}
				{failed.length > 0 && <Pill className="bg-warning-200 text-warning-700">{failed.length} Fehler</Pill>}
				{cancelled.length > 0 && (
					<Pill className="bg-caution-200 text-caution-700">{cancelled.length} abgebrochen</Pill>
				)}
				<Popover
					open={detailsOpen}
					onClose={() => setDetailsOpen(false)}
					align="end"
					size="l"
					trigger={
						<button
							type="button"
							onClick={() => setDetailsOpen((open) => !open)}
							className="inline-flex items-center gap-1 whitespace-nowrap rounded-control px-2 py-1 text-xs font-medium text-bgBase-800 hover:bg-bgBase-400 hover:text-text"
						>
							Details
							<BsChevronDown
								className={`h-3 w-3 transition-transform ${detailsOpen ? 'rotate-180' : ''}`}
							/>
						</button>
					}
				>
					<div className="max-h-64 overflow-y-auto p-2">
						{jobs.map((job) => (
							<div
								key={job.id}
								className="flex items-center gap-3 rounded-control px-2 py-1.5 hover:bg-bgBase-300"
							>
								<span
									className={`h-1.5 w-1.5 flex-shrink-0 rounded-full ${DOT_COLORS[job.status] || 'bg-primary-500'}`}
								/>
								<span className="min-w-0 flex-1 text-sm text-text">{jobLabel(job)}</span>
								<span className="flex-shrink-0 whitespace-nowrap text-xs tabular-nums text-bgBase-800">
									{jobStatusText(
										job,
										percentOf(job),
										progressByJobId[job.id]?.attempt ?? job.attempt ?? 0,
									)}
								</span>
							</div>
						))}
					</div>
				</Popover>
				{running.length === 0 && (
					<button
						type="button"
						onClick={() => {
							setDismissedAt(new Date());
							setDetailsOpen(false);
						}}
						title="Ausblenden"
						className="rounded-control p-1 text-bgBase-700 hover:bg-bgBase-400 hover:text-text"
					>
						<BsXLg className="h-3 w-3" />
					</button>
				)}
			</div>
		</div>
	);
};

JobProgressList.propTypes = {
	runningJobs: PropTypes.arrayOf(PropTypes.object),
	extraJobs: PropTypes.arrayOf(PropTypes.object),
};
